import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useSemester } from '../core/SemesterContext';
import axios from "axios";
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import { Icon } from '@iconify/react';
import NavHeader from "./NavHeader";
import CompareSardinesFilters from './CompareSardinesFilters';
import {
  parseCompareFiltersFromSearch,
  buildCompareFiltersSearch,
} from '../core/utils/compareSardinesFilterUtils';
import "../styles/general.css";
import "../styles/main.css";
import "../styles/CompareVolunteers.css";
import { URL } from '../core/config';
import SardineStatsCard from './SardinesStats/SardineStatsCard';
import StatsFilterOptions from './SardinesStats/StatsFilterOptions';
import LoadingSpinner from './LoadingSpinner';

Chart.register(ArcElement, Tooltip, Legend);

const usersURL = URL + "/users";

const getEmptyStats = () => ({
  eventsAsMO: 0,
  eventsAsVMO: 0,
  eventsAsStaff: 0,
  totalEvents: 0,
  ICEEvents: 0,
  STRIPSEvents: 0,
  EDUEvents: 0,
  PARTYEvents: 0,
  PCEvents: 0,
  HREvents: 0,
  OtherEvents: 0,
  shotsWon: 0,
  shotsSent: 0,
});


const SardinesComparison = () => {
  const semesterContext = useSemester();
  const semester = semesterContext.currentSemester;

  const location = useLocation();
  const history = useHistory();
  const goBack = () => history.push(location.state?.from || '/users-list');

  const filters = useMemo(
    () => parseCompareFiltersFromSearch(location.search),
    [location.search],
  );

  const sardineIds = filters.sardines;
  const [sardines, setSardines] = useState([]);
  const [allEventsData, setAllEventsData] = useState({});
  const [allOpenCallsData, setAllOpenCallsData] = useState({});
  const [filteredSardines, setFilteredSardines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtersVisible, setFiltersVisible] = useState(() => sardineIds.length === 0);

  const updateFilters = useCallback((nextFilters) => {
    const search = buildCompareFiltersSearch(nextFilters);
    history.replace(search ? `/compare-sardines?${search}` : '/compare-sardines');
  }, [history]);

  useEffect(() => {
    if (sardineIds.length === 0) {
      setSardines([]);
      setFilteredSardines([]);
      setAllEventsData({});
      setAllOpenCallsData({});
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchSardines = async () => {
      setLoading(true);

      try {
        const response = await axios.post(`${usersURL}/compare`, {
          userIds: sardineIds,
          semester: semester._id,
        });

        const {
          sardines: sardinesData,
          eventsByUser,
          openCallsByUser,
        } = response.data;

        const sardinesWithEvents = sardinesData.map((sardine) => ({
          ...sardine,
          stats: getEmptyStats(),
        }));

        if (!cancelled) {
          setAllEventsData(eventsByUser);
          setAllOpenCallsData(openCallsByUser);
          setSardines(sardinesWithEvents);
          setFilteredSardines(sardinesWithEvents);
        }
      } catch (error) {
        console.error('Error fetching sardines data:', error);
        if (!cancelled) {
          setSardines([]);
          setFilteredSardines([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchSardines();

    return () => {
      cancelled = true;
    };
  }, [sardineIds, semester._id]);

  const handleFilteredSorted = useCallback((nextFilteredSardines) => {
    setFilteredSardines(nextFilteredSardines);
  }, []);

  const hasSelectedSardines = sardineIds.length > 0;
  const hasResults = sardines.length > 0;

  return (
    <div className="page-container">
      <NavHeader />
      <h1 className='page-title'>Sardines Comparison</h1>
      <div className="compare-sardines-filters-panel section">
        <div className="compare-sardines-filters-panel-header">
          <h2 className="compare-sardines-filters-panel-title">
            <span className="main-section-icon">
              <span
                className="main-section-icon-fill"
                style={{ background: 'rgba(0, 174, 239, 0.15)', color: '#00aeef' }}
              >
                <Icon icon="material-symbols:compare-arrows" className="icon" />
              </span>
            </span>
            Comparison Filters
          </h2>
          <button
            type="button"
            className="compare-sardines-filters-toggle"
            onClick={() => setFiltersVisible((visible) => !visible)}
            aria-expanded={filtersVisible}
            aria-label={filtersVisible ? 'Hide comparison filters' : 'Show comparison filters'}
          >
            <Icon
              icon={filtersVisible ? 'mdi:filter-off-outline' : 'mdi:filter-outline'}
              className="icon"
            />
          </button>
        </div>

        {filtersVisible && (
          <CompareSardinesFilters
            values={filters}
            onChange={updateFilters}
            idPrefix="compare-page-"
          />
        )}
      </div>


      {loading && (
        <LoadingSpinner message="Loading sardines data" />
      )}

      {!loading && !hasSelectedSardines && (
        <div className="compare-sardines-empty-state section">
          <Icon icon="material-symbols:compare-arrows" className="compare-sardines-empty-icon" />
          <p>Select sardines using the filters above to start comparing.</p>
          <p className="compare-sardines-empty-hint">
            Combine department and membership filters to narrow results, or pick individual sardines.
          </p>
        </div>
      )}

      {!loading && hasSelectedSardines && !hasResults && (
        <div className="compare-sardines-empty-state section">
          <p>No sardine data could be loaded for the current selection.</p>
          <p className="compare-sardines-empty-hint">Try changing your filters above.</p>
        </div>
      )}

      {!loading && hasResults && (
        <>
          <StatsFilterOptions
            sardines={sardines}
            allEventsData={allEventsData}
            allOpenCallsData={allOpenCallsData}
            onFilteredSorted={handleFilteredSorted}
          />

          <div className="section">
            <div className="comparison-container">
              {filteredSardines.map((sardine) => (
                <SardineStatsCard key={sardine._id} sardine={sardine} />
              ))}
            </div>
          </div>
        </>
      )}

      <div className="button-container">
        <button
          className="button back-button"
          onClick={goBack}
        >
          Go Back
        </button>
      </div>
    </div>
  );
};

export default SardinesComparison;

